import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { validators, formats, dates } from "investira.sdk";
import {
  Chip,
  Stack,
  Tooltip,
  Typography,
  CircularProgress,
  IconButton,
  Icon,
} from "investira.react.components";
import { DataTable } from "../";
import services from "../../../services";
import { UPLOAD_TIPO } from "../../../enums";

const ArquivosDataGrid = (props) => {
  const { isFetching, data, onDeleteClick, ...restProps } = props;

  const xDataFormated = (pData) => {
    return pData.map((xRow) => {
      return {
        ...xRow,
        carteira_seuid:
          xRow.carteira_seuid == null ? "AUSENTE" : xRow.carteira_seuid,
        arquivo_formated: xRow.arquivo
          ? xRow.arquivo.replace(/\.xml$/i, "")
          : xRow.arquivo == null
          ? "AUSENTE"
          : xRow.arquivo,
        data_envio_formated: xRow.data_envio
          ? formats.formatDate(dates.toDate(xRow.data_envio))
          : "AUSENTE",
        ifeed_data_posicao: xRow.ifeed_data_posicao
          ? formats.formatDate(dates.toDate(xRow.ifeed_data_posicao))
          : "AUSENTE",
        ifeed_upload_fim: xRow.ifeed_upload_fim
          ? formats.formatDate(dates.toDate(xRow.ifeed_upload_fim))
          : "AUSENTE",
        origem: xRow.origem ? UPLOAD_TIPO[xRow.origem].label : "AUSENTE",
      };
    });
  };

  function onDownloadClick(pData) {
    if (pData) {
      services.posicoes.download(
        pData._rid,
        (rRes) => {
          let xOutputFilename = `${pData.arquivo}.xml`;

          if (rRes?.headers["content-disposition"]) {
            let xFilenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            let xMatches = xFilenameRegex.exec(
              rRes.headers["content-disposition"]
            );
            if (xMatches != null && xMatches[1]) {
              xOutputFilename = xMatches[1].replace(/['"]/g, "");
            }
          }

          const xFileURL = URL.createObjectURL(rRes.data);
          let xLinkElem = document.createElement("a");
          xLinkElem.href = xFileURL;
          xLinkElem.download = xOutputFilename;
          xLinkElem.click();
          URL.revokeObjectURL(xFileURL);
          xLinkElem.remove();
        },
        (rErr) => {
          console.log(rErr);
        }
      );
    }
  }

  const columns = useMemo(
    () => [
      {
        accessorKey: "data_envio_formated",
        header: "Envio",
        enableColumnActions: false,
        enableSorting: false,
        maxSize: 100,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }
          return cell.getValue();
        },
      },
      {
        accessorKey: "arquivo_formated",
        header: "Arquivo",
        enableColumnActions: false,
        enableSorting: false,
        enableColumnFilter: false,
        //filterVariant: "select",
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }
          return <Typography variant="body2">{cell.getValue()}</Typography>;
        },
      },
      {
        id: "status",
        accessorKey: "ifeed_upload_fim",
        header: "Status",
        enableColumnActions: false,
        enableSorting: false,
        maxSize: 80,
        enableColumnFilter: false,
        muiTableHeadCellProps: () => {
          return { align: "center" };
        },
        muiTableBodyCellProps: () => {
          return { align: "center" };
        },
        Cell: (xProps) => {
          const { cell, row } = xProps;

          if (!validators.isEmpty(row.original.ifeed_erros)) {
            const xError = "Erro";

            return (
              <Tooltip
                arrow
                title={
                  <Stack
                    spacing={1}
                    sx={{
                      px: 2,
                      py: 1,
                      whiteSpace: "normal",
                    }}
                  >
                    {validators.isArray(row.original.ifeed_erros) ? (
                      row.original.ifeed_erros.map((error, index) => {
                        return (
                          <Stack key={`file-error-${index}`} spacing={1}>
                            <Typography variant="caption" color="textPrimary">
                              {error.description || error}
                            </Typography>
                          </Stack>
                        );
                      })
                    ) : (
                      <Typography variant="caption" color="textPrimary">
                        {row.original.ifeed_erros.description ||
                          JSON.stringify(row.original.ifeed_erros.erros)}
                      </Typography>
                    )}
                  </Stack>
                }
              >
                <span>
                  <Chip
                    size="small"
                    label={xError}
                    color="error"
                    sx={{ cursor: "help" }}
                  />
                </span>
              </Tooltip>
            );
          }

          if (!validators.isNull(cell.getValue())) {
            return <Chip size="small" label="Ok" color="success" />;
          }

          if (!validators.isNull(row.original.arquivo)) {
            return <CircularProgress size={24} />;
          }

          return null;
        },
      },
      {
        id: "origem",
        accessorKey: "origem",
        header: "Origem",
        enableColumnActions: false,
        enableSorting: false,
        filterVariant: "select",
        maxSize: 100,
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return "";
          }
          return cell.getValue();
        },
      },
      {
        id: "carteira_seuid",
        accessorKey: "carteira_seuid",
        header: "Carteira",
        enableColumnActions: false,
        enableSorting: false,
        filterVariant: "select",
      },
      {
        id: "posicao",
        accessorKey: "ifeed_data_posicao",
        header: "Posição",
        enableColumnActions: false,
        enableSorting: false,
        //filterVariant: "select",
        maxSize: 100,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return "";
          }
          return cell.getValue();
        },
      },
    ],
    []
  );

  const xProps = useMemo(() => {
    return {
      columns,
      ...restProps,
      enableFacetedValues: true,
      enableRowSelection: true,
      enableGlobalFilter: false,
      enableExpanding: false,
      enableFullScreenToggle: false,
      enableDensityToggle: false,
      enableBottomToolbar: true,
      muiTopToolbarProps: {
        sx: {
          backgroundColor: "none",
          alignItems: "center",
        },
      },
      initialState: {
        ...props.initialState,
      },
      renderRowActions: ({ row }) => {
        return (
          <Stack direction="row">
            <Tooltip title="Download">
              <IconButton
                onClick={() => onDownloadClick(row.original)}
                color="primary"
              >
                <Icon iconName="download" size={21} />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    };
  }, [columns, props.initialState, restProps]);

  return <DataTable data={xDataFormated(data)} {...xProps} />;
};

ArquivosDataGrid.propTypes = {
  data: PropTypes.array,
  isFetching: PropTypes.bool,
  initialState: PropTypes.object,
};

ArquivosDataGrid.default = {
  initialState: {},
};

export default ArquivosDataGrid;
